// Full-screen pack opening: 5 cards in a circle, tap to flip, Reveal all.

import React, { useEffect, useMemo, useState } from "react";
import { getCardImageSrc } from "../utils/cardImages";
import {
    chunkPackDrops,
    formatPackDropLabel,
    getPackDropBadge,
    getPackDropHintClasses,
    getPackRevealLayout,
    isPackDropSpecial,
} from "../utils/packReveal";
import { CORGI_PACK_SIZE, formatTreats } from "../utils/treats";
import "./row/row.css";

const PACK_RADIUS_PX = 118;
const PACK_RADIUS_COMPACT_PX = 92;

const StorePackRevealModal = ({
    packResult = null,
    onClose,
    onBuyMore,
    onOpenNextPack,
    remainingInventoryPacks = 0,
}) => {
    const drops = packResult?.drops || [];
    const pages = useMemo(() => chunkPackDrops(drops), [drops]);
    const [pageIndex, setPageIndex] = useState(0);
    const [revealedKeys, setRevealedKeys] = useState(() => new Set());

    const sessionKey = packResult?.openedAt || "pack";
    const remainingPacks = Math.max(
        0,
        Math.floor(Number(remainingInventoryPacks) || 0)
    );
    const canOpenNext = remainingPacks > 0 && typeof onOpenNextPack === "function";

    useEffect(() => {
        setPageIndex(0);
        setRevealedKeys(new Set());
    }, [sessionKey]);

    useEffect(() => {
        const { body, documentElement } = document;
        const previousBodyOverflow = body.style.overflow;
        const previousBodyTouchAction = body.style.touchAction;
        const previousHtmlOverflow = documentElement.style.overflow;
        const previousHtmlTouchAction = documentElement.style.touchAction;

        body.style.overflow = "hidden";
        body.style.touchAction = "none";
        documentElement.style.overflow = "hidden";
        documentElement.style.touchAction = "none";

        return () => {
            body.style.overflow = previousBodyOverflow;
            body.style.touchAction = previousBodyTouchAction;
            documentElement.style.overflow = previousHtmlOverflow;
            documentElement.style.touchAction = previousHtmlTouchAction;
        };
    }, []);

    const currentPage = pages[pageIndex] || [];
    const pageStartIndex = pageIndex * CORGI_PACK_SIZE;
    const radiusPx =
        currentPage.length <= 3 ? PACK_RADIUS_COMPACT_PX : PACK_RADIUS_PX;
    const layout = useMemo(
        () => getPackRevealLayout(currentPage.length, radiusPx),
        [currentPage.length, radiusPx]
    );

    const pageKeys = currentPage.map(
        (_, localIndex) => `${sessionKey}-${pageStartIndex + localIndex}`
    );
    const allPageRevealed = pageKeys.every((key) => revealedKeys.has(key));
    const allRevealed =
        drops.length > 0 &&
        drops.every((_, index) => revealedKeys.has(`${sessionKey}-${index}`));
    const isLastPage = pageIndex >= pages.length - 1;

    if (!drops.length) {
        return null;
    }

    const revealKey = (key) => {
        setRevealedKeys((prev) => {
            if (prev.has(key)) {
                return prev;
            }
            const next = new Set(prev);
            next.add(key);
            return next;
        });
    };

    const revealCurrentPage = () => {
        setRevealedKeys((prev) => {
            const next = new Set(prev);
            pageKeys.forEach((key) => next.add(key));
            return next;
        });
    };

    const revealAll = () => {
        setRevealedKeys(
            new Set(drops.map((_, index) => `${sessionKey}-${index}`))
        );
    };

    const handleContinue = () => {
        if (!allPageRevealed) {
            return;
        }
        if (!isLastPage) {
            setPageIndex((value) => value + 1);
            return;
        }
        if (typeof onClose === "function") {
            onClose();
        }
    };

    const handleSecondary = () => {
        if (canOpenNext) {
            onOpenNextPack();
            return;
        }
        if (typeof onBuyMore === "function") {
            onBuyMore();
            return;
        }
        if (typeof onClose === "function") {
            onClose();
        }
    };

    const packCount = Math.max(1, Number(packResult.packCount) || 1);
    const title =
        pages.length > 1
            ? `Pack ${pageIndex + 1} of ${pages.length}`
            : packCount > 1
              ? `${packCount} Corgi Packs`
              : "Corgi Pack";

    const summaryParts = [
        packResult.newCardCount
            ? `${packResult.newCardCount} new`
            : null,
        packResult.newFoilCount
            ? `${packResult.newFoilCount} new foil`
            : null,
    ].filter(Boolean);
    const refundAmount = Math.max(0, Math.floor(Number(packResult.refund) || 0));
    const showResults = allRevealed && (summaryParts.length > 0 || refundAmount > 0);

    return (
        <div
            className="packRevealOverlay"
            role="dialog"
            aria-modal="true"
            aria-labelledby="packRevealTitle"
        >
            <div className="packRevealOverlay__panel">
                <div id="packRevealTitle" className="packRevealOverlay__title">
                    {title}
                </div>
                <p className="packRevealOverlay__hint">
                    {allPageRevealed
                        ? isLastPage
                          ? "All cards revealed."
                          : "Pack revealed — continue to the next pack."
                        : "Tap a card to flip it. Special cards use a marked back and shake while face down."}
                </p>

                <div className="packRevealCircle">
                    {currentPage.map((drop, localIndex) => {
                        const globalIndex = pageStartIndex + localIndex;
                        const key = `${sessionKey}-${globalIndex}`;
                        const revealed = revealedKeys.has(key);
                        const hintClasses = getPackDropHintClasses(drop);
                        const hintClass = hintClasses.join(" ");
                        const slotHintClass = hintClasses
                            .map((name) =>
                                name.replace(
                                    "packRevealCard--",
                                    "packRevealCardSlot--"
                                )
                            )
                            .join(" ");
                        const slot = layout[localIndex] || { x: 0, y: 0 };
                        const badge = revealed ? getPackDropBadge(drop) : "";
                        const label = formatPackDropLabel(drop);

                        return (
                            <div
                                key={key}
                                className={`packRevealCardSlot${
                                    revealed
                                        ? " packRevealCardSlot--revealed"
                                        : ""
                                }${slotHintClass ? ` ${slotHintClass}` : ""}`}
                                style={{
                                    left: `calc(50% + ${slot.x}px)`,
                                    top: `calc(50% + ${slot.y}px)`,
                                }}
                            >
                                <div
                                    className={`packRevealCard${
                                        hintClass ? ` ${hintClass}` : ""
                                    }${
                                        revealed
                                            ? " packRevealCard--revealed"
                                            : ""
                                    }`}
                                >
                                    <div className="cardFlip packRevealCard__flip">
                                        <div
                                            className="cardFlipInner packRevealCard__flipInner"
                                            style={{
                                                transform: `rotateY(${
                                                    revealed ? 0 : 180
                                                }deg)`,
                                                WebkitTransform: `rotateY(${
                                                    revealed ? 0 : 180
                                                }deg)`,
                                            }}
                                        >
                                            <button
                                                type="button"
                                                className={`cardFlipBack packRevealCard__face packRevealCard__face--back${
                                                    hintClasses.includes(
                                                        "packRevealCard--special"
                                                    )
                                                        ? " packRevealCard__face--backSpecial"
                                                        : ""
                                                }${
                                                    !revealed && hintClass
                                                        ? ` ${hintClass}`
                                                        : ""
                                                }`}
                                                onClick={() => revealKey(key)}
                                                disabled={revealed}
                                                aria-label={
                                                    revealed
                                                        ? `${label}${
                                                              badge
                                                                  ? `, ${badge}`
                                                                  : ""
                                                          }`
                                                        : hintClasses.includes(
                                                                "packRevealCard--special"
                                                            )
                                                          ? "Special face-down card — tap to reveal"
                                                          : "Face-down card — tap to reveal"
                                                }
                                            />
                                            <img
                                                src={getCardImageSrc(drop)}
                                                alt=""
                                                className="card-image cardFlipFront packRevealCard__face packRevealCard__face--front"
                                            />
                                            {drop.isFoil && revealed ? (
                                                <span
                                                    className="packRevealCard__foilWrap collectionImageFoilWrap"
                                                    aria-hidden="true"
                                                />
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className="packRevealCard__meta">
                                        <div className="packRevealCard__name">
                                            {revealed ? label : "\u00a0"}
                                        </div>
                                        <div className="packRevealCard__badge">
                                            {badge || "\u00a0"}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                <div className="packRevealOverlay__results" aria-live="polite">
                    {showResults ? (
                        <>
                            {summaryParts.length ? (
                                <p className="packRevealOverlay__summary">
                                    {summaryParts.join(" · ")}
                                </p>
                            ) : null}
                            {refundAmount > 0 ? (
                                <p className="packRevealOverlay__refund">
                                    +{formatTreats(refundAmount)} back from
                                    duplicates
                                </p>
                            ) : null}
                        </>
                    ) : (
                        <p className="packRevealOverlay__summary"> </p>
                    )}
                </div>

                <div className="packRevealOverlay__footer">
                    <div className="packRevealOverlay__actions">
                    {!allRevealed ? (
                        <button
                            type="button"
                            className="homePrimaryButton homeSecondaryButton"
                            onClick={revealAll}
                        >
                            Reveal all
                        </button>
                    ) : null}
                    {!allPageRevealed ? (
                        <button
                            type="button"
                            className="homePrimaryButton homeSecondaryButton"
                            onClick={revealCurrentPage}
                        >
                            Reveal this pack
                        </button>
                    ) : null}
                    {allPageRevealed ? (
                        <button
                            type="button"
                            className="homePrimaryButton"
                            onClick={handleContinue}
                        >
                            {isLastPage ? "Done" : "Next pack"}
                        </button>
                    ) : null}
                    {allPageRevealed && isLastPage ? (
                        <button
                            type="button"
                            className="homePrimaryButton homeSecondaryButton"
                            onClick={handleSecondary}
                        >
                            {canOpenNext ? "Open next pack" : "Buy more"}
                        </button>
                    ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StorePackRevealModal;
